import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { ProcessReceiveProps, ProcessReceiveFilterType } from "../../types/processReceive";
import { PartyOptionProps, ItemOptionProps } from "../../types/processOrder";

export interface ProcessReceivesState {
  receives: ProcessReceiveProps[];
  totalReceives: number;
  totalAmount: number;
  filter: ProcessReceiveFilterType;
  partyOptions: PartyOptionProps[];
  itemOptions: ItemOptionProps[];
  loading: boolean;
  error: string | null;
}

const initialState: ProcessReceivesState = {
  receives: [],
  totalReceives: 0,
  totalAmount: 0,
  filter: {
    search: null,
    party_id: null,
    item_id: null,
    fromDate: null,
    toDate: null,
    sortBy: "id",
    sortOrder: "desc",
  },
  partyOptions: [],
  itemOptions: [],
  loading: false,
  error: null,
};

type FetchListProps = {
  process_definition_id: number;
  page: number;
  size?: number;
  search?: string | null;
  party_id?: number | null;
  item_id?: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  sortBy?: string;
  sortOrder?: string;
};

export const fetchProcessReceivesList = createAsyncThunk(
  "processReceives/fetchList",
  async (req: FetchListProps) => {
    let response: any;
    await GET(`/v1/process-receives`, { ...req })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessReceiveParties = createAsyncThunk(
  "processReceives/fetchParties",
  async (process_definition_id: number) => {
    let response: any;
    await GET(`/v1/process-receives-parties`, { process_definition_id })
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const fetchProcessReceiveItems = createAsyncThunk(
  "processReceives/fetchItems",
  async () => {
    let response: any;
    await GET(`/v1/process-orders-items`, {})
      .then((res: any) => { response = res; })
      .catch((err) => { response = err; });
    return response;
  }
);

export const processReceivesSlice = createSlice({
  name: "processReceives",
  initialState,
  reducers: {
    setProcessReceivesFilter: (state, action: PayloadAction<Partial<ProcessReceiveFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProcessReceivesList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProcessReceivesList.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.receives = action.payload.data.data.records;
        state.totalReceives = action.payload.data.data.total_records;
        state.totalAmount = action.payload.data.data.total_amount;
        state.error = null;
      } else {
        state.receives = [];
        state.error = action?.payload?.data?.message || "Something went wrong.";
      }
    });
    builder.addCase(fetchProcessReceivesList.rejected, (state, action: any) => {
      state.receives = [];
      state.loading = false;
      state.error = action?.payload?.data?.message || "Something went wrong.";
    });
    builder.addCase(fetchProcessReceiveParties.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.partyOptions = action.payload.data.data.map((p: any) => ({
          value: p.id,
          label: p.firm_name || p.name,
        }));
      }
    });
    builder.addCase(fetchProcessReceiveItems.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.itemOptions = action.payload.data.data.map((i: any) => ({
          value: i.id,
          label: i.name,
        }));
      }
    });
  },
});

export const { setProcessReceivesFilter } = processReceivesSlice.actions;

export const getProcessReceives = (state: RootState) => state.processReceives.receives;
export const getTotalProcessReceives = (state: RootState) => state.processReceives.totalReceives;
export const getTotalProcessReceiveAmount = (state: RootState) => state.processReceives.totalAmount;
export const getProcessReceivesFilter = (state: RootState) => state.processReceives.filter;
export const getProcessReceivePartyOptions = (state: RootState) => state.processReceives.partyOptions;
export const getProcessReceiveItemOptions = (state: RootState) => state.processReceives.itemOptions;
export const getProcessReceivesLoading = (state: RootState) => state.processReceives.loading;

export default processReceivesSlice.reducer;
